const WORKFLOW_STAGE_LABELS = {
	1: 'Initial Notice',
	2: 'Upload Restricted',
	3: 'Locked',
}

export function getDelinquencyPills({
	isAccountBillingRestricted,
	manualDelinquentStoreCount = 0,
	billingRestrictedStoreCount = 0,
	hasOutstandingBalance = false,
	isPendingReview = false,
	accountWorkflowStage = 0,
} = {}) {
	const pills = []

	if (hasOutstandingBalance) {
		pills.push({ key: 'balance', label: 'Outstanding balance' })
	}

	if (isPendingReview) {
		pills.push({ key: 'pending-review', label: 'Flagged' })
	}

	if (isAccountBillingRestricted) {
		const stageLabel = WORKFLOW_STAGE_LABELS[accountWorkflowStage]
		pills.push({
			key: 'account-billing',
			label: stageLabel || 'Account billing restricted',
		})
	} else if (billingRestrictedStoreCount > 0) {
		pills.push({
			key: 'store-billing',
			label:
				billingRestrictedStoreCount === 1
					? '1 store billing restricted'
					: `${billingRestrictedStoreCount} stores billing restricted`,
		})
	}

	if (manualDelinquentStoreCount > 0) {
		pills.push({
			key: 'manual',
			label:
				manualDelinquentStoreCount === 1
					? '1 manual delinquency'
					: `${manualDelinquentStoreCount} manual delinquencies`,
		})
	}

	return pills
}

export const ALL_BILLING_STATUSES = 'all'

// Each option is one mutually exclusive view. An outstanding balance has no option of its own:
// it is always either Flagged (no workflow yet) or in one of the workflow stages.
export const BILLING_STATUS_OPTIONS = [
	{ value: ALL_BILLING_STATUSES, label: 'All billing statuses', params: {} },
	{ value: 'flagged', label: 'Flagged', params: { awaitingDecision: true } },
	...Object.entries(WORKFLOW_STAGE_LABELS).map(([stage, label]) => ({
		value: `stage-${stage}`,
		label: `${label}`,
		params: { workflowStage: stage },
	})),
]

// Every billing filter param, including legacy ones, so picking an option clears the rest.
// manualDelinquent (legacy Store.IsDelinquent) is still an API filter but has no dropdown option:
// it isn't a billing status.
const BILLING_FILTER_KEYS = ['delinquent', 'outstandingBalance', 'awaitingDecision', 'workflowStage', 'manualDelinquent']

export const toBillingStatusFilters = (value) => {
	const option = BILLING_STATUS_OPTIONS.find((o) => o.value === value) ?? BILLING_STATUS_OPTIONS[0]
	return {
		...Object.fromEntries(BILLING_FILTER_KEYS.map((key) => [key, null])),
		...option.params,
	}
}

// Hide the filter while detection is off; keep it if a billing status is already selected so it can be cleared.
export const shouldShowBillingStatusFilter = (accounts, filters = {}) =>
	accounts?.isBillingDelinquencyDetectionEnabled === true ||
	getBillingStatusValue(filters) !== ALL_BILLING_STATUSES

export const getBillingStatusValue = (filters = {}) => {
	const match = BILLING_STATUS_OPTIONS.find(
		(option) =>
			option.value !== ALL_BILLING_STATUSES &&
			Object.entries(option.params).every(([key, value]) => String(filters[key]) === String(value))
	)
	return match?.value ?? ALL_BILLING_STATUSES
}
